import clsx from 'clsx';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import AnimateHeight from 'react-animate-height';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {Checkbox} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import {ErrorMessages} from '@/views/campaigns/brief/brief-field-manager/BriefFieldManager';
import BriefFieldInput from '@/views/campaigns/brief/components/brief-field-input';
import BriefSubSection from '@/views/campaigns/brief/components/campaign-brief/components/brief-sub-section';
import ValidatedField from '@/views/campaigns/brief/components/campaign-brief/components/validated-field';
import {
  SaveCampaign,
  SectionProps,
} from '@/views/campaigns/brief/components/campaign-brief/section/SectionProps';
import CAMPAIGN_BRIEF_TEST_IDS from '@/views/campaigns/brief/components/campaign-brief/testIds';
import {
  CREATOR_COMPENSATION_TYPES,
  DEFAULT_SAVE_CAMPAIGN_DELAY,
} from '@/views/campaigns/brief/constants';
import styles from './CreatorPayments.module.scss';
import PaymentRecap from './PaymentRecap';
import ShopifyLinkModal from './ShopifyLinkModal';

const checkboxCheckedSrc = '/assets/svg/checkbox-checked.svg';
const checkboxUncheckedSrc = '/assets/svg/checkbox-unchecked.svg';

const TRANSLATION_PREFIX =
  'components.campaign-brief.sections.creator-payments';

const AFFILIATE_SALES_PERCENTAGES = [5, 10, 20];

const updateCreatorCompensationType = (value: string, updateBriefForm: any) => {
  updateBriefForm?.(
    'creatorCompensationType',
    value,
    'creatorCompensationType',
    DEFAULT_SAVE_CAMPAIGN_DELAY
  );
};

function CreatorCompensationRadioOption({
  checked,
  disabled,
  title,
  info,
  onClick,
  children,
}: {
  checked: boolean;
  disabled: boolean;
  title: string;
  info: string;
  onClick: (value: boolean) => void;
  children: React.ReactNode;
}) {
  return (
    <div className={styles.creatorCompensationRadioOption}>
      <div className={styles.checkbox}>
        <Checkbox
          radio
          disabled={disabled}
          checked={checked}
          onChange={onClick}
          label={
            <div
              className={`${styles.creatorCompensationRadioOptionHeader} ${
                disabled ? styles.disabled : ''
              }`}
              onClick={!disabled ? () => onClick(true) : undefined}
            >
              <label className={styles.title}>{title}</label>
              <label className={`${styles.info} ${styles.small}`}>{info}</label>
            </div>
          }
        />
      </div>
      <AnimateHeight
        className={styles.creatorCompensationRadioOptionContent}
        duration={500}
        height={checked ? 'auto' : 0}
      >
        {children}
      </AnimateHeight>
    </div>
  );
}

function CreatorCompensationCheckboxOption({
  checked,
  disabled = false,
  onChange,
  title,
  info,
  children,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
  title: string;
  info: string;
  children?: React.ReactNode;
}) {
  return (
    <div className={styles.paymentOptionContainer}>
      <div className={styles.checkboxContainer}>
        <Checkbox
          disabled={disabled}
          checked={checked}
          onChange={onChange}
          customCheckedIcon={<img src={checkboxCheckedSrc} alt="" />}
          customUncheckedIcon={<img src={checkboxUncheckedSrc} alt="" />}
        />
        <div className={styles.checkboxTextContainer}>
          <label className={clsx(styles.title, disabled && styles.disabled)}>
            {title}
          </label>
          <label
            className={clsx(
              styles.info,
              styles.small,
              disabled && styles.disabled
            )}
          >
            {info}
          </label>
        </div>
      </div>
      <div className={clsx(styles.checkboxContent, !checked && styles.hidden)}>
        {children}
      </div>
    </div>
  );
}

function CreatorCompensation({
  editable,
  budgetMin,
  budgetMax,
  budgetFixed,
  updateBriefForm,
  saveCampaign,
  onChange,
  errors,
  briefFieldManager,
  campaignId,
  hasShopifyAccessToken,
  brandId,
  allowAffiliateCampaigns,
}: {
  editable?: boolean;
  budgetMin?: number | null;
  budgetMax?: number | null;
  budgetFixed?: number | null;
  updateBriefForm?: (
    path: string,
    value: any,
    briefFieldKey?: string,
    saveCampaignDelay?: number
  ) => void;
  saveCampaign?: SaveCampaign;
  onChange?: (path: string, value: any) => void;
  errors?: ErrorMessages;
  briefFieldManager: any;
  campaignId: string;
  hasShopifyAccessToken: boolean;
  brandId: string;
  allowAffiliateCampaigns?: boolean;
}) {
  const budgetRangeData = useMemo(() => {
    return {min: budgetMin, max: budgetMax, fixed: budgetFixed};
  }, [budgetMin, budgetMax, budgetFixed]);

  const commissionInputRef = useRef<HTMLInputElement>(null);
  const [customCommissionRateInput, setCustomCommissionRateInput] =
    useState<string>('');
  const [isShopifyLinkModalOpen, setIsShopifyLinkModalOpen] = useState(false);

  const handleDeliverablesChange = (checked: boolean) => {
    if (!checked) {
      onChange?.('suggestedCostForCreations', null);
      onChange?.('suggestedCostForCreationsMin', null);
      onChange?.('fixedCostForCreations', null);
    }
    updateBriefForm?.(
      'creatorCompensationDeliverables',
      checked,
      'creatorCompensationDeliverables',
      DEFAULT_SAVE_CAMPAIGN_DELAY
    );
  };

  const handleAffiliateSalesChange = (checked: boolean) => {
    if (updateBriefForm) {
      if (!checked) {
        onChange?.('commissionRate', '');
      }
      updateBriefForm(
        'creatorCompensationAffiliateSales',
        checked,
        'creatorCompensationAffiliateSales',
        DEFAULT_SAVE_CAMPAIGN_DELAY
      );
    }
  };

  const getCashValue = (
    label: string,
    path: string,
    value: number | null | undefined,
    testID: string
  ) => {
    function getErrors(returnErrorsIfBothValuesAreFilled = false) {
      const bothValuesAreFilled =
        typeof budgetMin === 'number' && typeof budgetMax === 'number';
      switch (path) {
        case 'suggestedCostForCreationsMin':
          if (!value) {
            return errors?.[path];
          }
          return bothValuesAreFilled || returnErrorsIfBothValuesAreFilled
            ? errors?.[path]
            : [];
        case 'suggestedCostForCreations':
          if (!value) {
            return errors?.[path];
          }
          return returnErrorsIfBothValuesAreFilled ? errors?.[path] : [];
        case 'fixedCostForCreations':
          return errors?.[path];
        default:
          return errors?.[path];
      }
    }

    function getOnInputChange() {
      switch (path) {
        case 'suggestedCostForCreationsMin':
          return (_value: string | number) => {
            onChange?.('fixedCostForCreations', '');
            onChange?.(path, _value ? Number(_value) : _value);
          };
        case 'suggestedCostForCreations':
          return (_value: string | number) => {
            onChange?.('fixedCostForCreations', '');
            onChange?.(path, _value ? Number(_value) : _value);
          };
        case 'fixedCostForCreations':
          return (_value: string | number) => {
            onChange?.('suggestedCostForCreationsMin', '');
            onChange?.('suggestedCostForCreations', '');
            onChange?.(path, _value ? Number(_value) : _value);
          };
        default:
          return (_value: string | number) =>
            onChange?.(path, _value ? Number(_value) : _value);
      }
    }

    function getFieldName() {
      switch (path) {
        case 'suggestedCostForCreationsMin':
          return briefFieldManager.fields.suggestedCostForCreationsMin.key;
        case 'suggestedCostForCreations':
          return `${briefFieldManager.fields.suggestedCostForCreations.key}Max`;
        case 'fixedCostForCreations':
          return briefFieldManager.fields.fixedCostForCreations.key;
        default:
          return '';
      }
    }

    function getFieldValue() {
      switch (path) {
        case 'suggestedCostForCreationsMin':
          return briefFieldManager.fields.suggestedCostForCreationsMin.value;
        case 'suggestedCostForCreations':
          return briefFieldManager.fields.suggestedCostForCreations.value;
        case 'fixedCostForCreations':
          return briefFieldManager.fields.fixedCostForCreations.value;
        default:
          return '';
      }
    }

    let inputValue;

    if (path === 'fixedCostForCreations') {
      if (value !== null && value !== undefined) {
        inputValue = value;
      } else {
        inputValue = '';
      }
    } else {
      inputValue = value || '';
    }

    return (
      <ValidatedField errors={getErrors()}>
        <BriefFieldInput
          testID={testID}
          label={label}
          disabled={!editable}
          placeholder="0.00"
          value={inputValue}
          onInputChange={getOnInputChange()}
          endIcon={
            <label className={styles.currency}>
              {translate(`${TRANSLATION_PREFIX}.currency`)}
            </label>
          }
          inputProps={{
            type: 'number',
            min: 0,
            value: inputValue,
            onBlur: () =>
              saveCampaign?.({
                campaignId,
                fieldName: getFieldName(),
                fieldValue: getFieldValue(),
              }),
          }}
          hasErrors={!!getErrors(true)?.length}
        />
      </ValidatedField>
    );
  };

  const handleCustomCommissionClick = () => {
    updateBriefForm?.(
      'commissionRate',
      customCommissionRateInput,
      'commissionRate',
      DEFAULT_SAVE_CAMPAIGN_DELAY
    );
    commissionInputRef.current?.focus();
  };

  const isOtherCommissionSelected = !AFFILIATE_SALES_PERCENTAGES.includes(
    briefFieldManager.fields.commissionRate.value
  );

  useEffect(() => {
    const currentRate = briefFieldManager.fields.commissionRate.value;
    if (!AFFILIATE_SALES_PERCENTAGES.includes(currentRate)) {
      setCustomCommissionRateInput(String(currentRate ?? ''));
    }
  }, [briefFieldManager.fields.commissionRate.value]);

  return (
    <BriefSubSection
      disabled={!editable}
      title={translate(`${TRANSLATION_PREFIX}.creator-compensation`)}
      titleClassName={styles.title}
    >
      <ShopifyLinkModal
        brandId={brandId}
        isOpen={isShopifyLinkModalOpen}
        onClose={() => setIsShopifyLinkModalOpen(false)}
      />
      <div className={styles.creatorCompensationRangeContainer}>
        <label
          className={styles.info}
          dangerouslySetInnerHTML={{
            __html: allowAffiliateCampaigns
              ? translate(
                  `${TRANSLATION_PREFIX}.creator-compensation-info-affiliates`
                )
              : translate(`${TRANSLATION_PREFIX}.creator-compensation-info`),
          }}
        />
        <div>
          {allowAffiliateCampaigns ? (
            <ValidatedField
              errors={errors?.creatorCompensationDeliverables || []}
            >
              <CreatorCompensationCheckboxOption
                checked={
                  !!briefFieldManager.fields.creatorCompensationDeliverables
                    .value
                }
                onChange={(checked: boolean) =>
                  handleDeliverablesChange(checked)
                }
                title={translate(`${TRANSLATION_PREFIX}.deliverables`)}
                info={translate(`${TRANSLATION_PREFIX}.deliverables-info`)}
              >
                <div className={styles.radioOptionsContainer}>
                  <div
                    className={clsx(
                      styles.radioOptionContainer,
                      briefFieldManager.fields.creatorCompensationType.value ===
                        CREATOR_COMPENSATION_TYPES.RANGE && styles.selected
                    )}
                  >
                    <CreatorCompensationRadioOption
                      checked={
                        briefFieldManager.fields.creatorCompensationType
                          .value === CREATOR_COMPENSATION_TYPES.RANGE
                      }
                      disabled={!editable}
                      title={translate(`${TRANSLATION_PREFIX}.suggested-range`)}
                      info={translate(
                        `${TRANSLATION_PREFIX}.suggested-range-info`
                      )}
                      onClick={() =>
                        updateCreatorCompensationType(
                          CREATOR_COMPENSATION_TYPES.RANGE,
                          updateBriefForm
                        )
                      }
                    >
                      <div className={`${styles.row} ${styles.rowGap}`}>
                        <div className={styles.column}>
                          <div className={`${styles.cashValue} ${styles.big}`}>
                            {getCashValue(
                              translate(`${TRANSLATION_PREFIX}.min`),
                              'suggestedCostForCreationsMin',
                              budgetRangeData.min,
                              CAMPAIGN_BRIEF_TEST_IDS.CREATOR_PAYMENTS__COMPENSATION_MIN
                            )}
                          </div>
                        </div>
                        <div className={styles.column}>
                          <div className={`${styles.cashValue} ${styles.big}`}>
                            {getCashValue(
                              translate(`${TRANSLATION_PREFIX}.max`),
                              'suggestedCostForCreations',
                              budgetRangeData.max,
                              CAMPAIGN_BRIEF_TEST_IDS.CREATOR_PAYMENTS__COMPENSATION_MAX
                            )}
                          </div>
                        </div>
                      </div>
                    </CreatorCompensationRadioOption>
                  </div>
                  <div
                    className={clsx(
                      styles.radioOptionContainer,
                      briefFieldManager.fields.creatorCompensationType.value ===
                        CREATOR_COMPENSATION_TYPES.FIXED && styles.selected
                    )}
                  >
                    <CreatorCompensationRadioOption
                      checked={
                        briefFieldManager.fields.creatorCompensationType
                          .value === CREATOR_COMPENSATION_TYPES.FIXED
                      }
                      disabled={!editable}
                      title={translate(`${TRANSLATION_PREFIX}.fixed-price`)}
                      info={translate(`${TRANSLATION_PREFIX}.fixed-price-info`)}
                      onClick={() =>
                        updateCreatorCompensationType(
                          CREATOR_COMPENSATION_TYPES.FIXED,
                          updateBriefForm
                        )
                      }
                    >
                      <div className={`${styles.row} ${styles.rowGap}`}>
                        <label className={`${styles.info} ${styles.small}`}>
                          {translate(
                            `${TRANSLATION_PREFIX}.enter-payment-amount`
                          )}
                        </label>
                      </div>
                      <div className={`${styles.row} ${styles.rowGap}`}>
                        <div className={styles.column}>
                          <div className={`${styles.cashValue} ${styles.big}`}>
                            {getCashValue(
                              '',
                              'fixedCostForCreations',
                              budgetRangeData.fixed,
                              CAMPAIGN_BRIEF_TEST_IDS.CREATOR_PAYMENTS__COMPENSATION_FIXED
                            )}
                          </div>
                        </div>
                      </div>
                    </CreatorCompensationRadioOption>
                  </div>
                </div>
              </CreatorCompensationCheckboxOption>
            </ValidatedField>
          ) : (
            <div className={styles.radioOptionsContainer}>
              <div
                className={clsx(
                  styles.radioOptionContainer,
                  briefFieldManager.fields.creatorCompensationType.value ===
                    CREATOR_COMPENSATION_TYPES.RANGE && styles.selected
                )}
              >
                <CreatorCompensationRadioOption
                  checked={
                    briefFieldManager.fields.creatorCompensationType.value ===
                    CREATOR_COMPENSATION_TYPES.RANGE
                  }
                  disabled={!editable}
                  title={translate(`${TRANSLATION_PREFIX}.suggested-range`)}
                  info={translate(`${TRANSLATION_PREFIX}.suggested-range-info`)}
                  onClick={() =>
                    updateCreatorCompensationType(
                      CREATOR_COMPENSATION_TYPES.RANGE,
                      updateBriefForm
                    )
                  }
                >
                  <div className={`${styles.row} ${styles.rowGap}`}>
                    <div className={styles.column}>
                      <div className={`${styles.cashValue} ${styles.big}`}>
                        {getCashValue(
                          translate(`${TRANSLATION_PREFIX}.min`),
                          'suggestedCostForCreationsMin',
                          budgetRangeData.min,
                          CAMPAIGN_BRIEF_TEST_IDS.CREATOR_PAYMENTS__COMPENSATION_MIN
                        )}
                      </div>
                    </div>
                    <div className={styles.column}>
                      <div className={`${styles.cashValue} ${styles.big}`}>
                        {getCashValue(
                          translate(`${TRANSLATION_PREFIX}.max`),
                          'suggestedCostForCreations',
                          budgetRangeData.max,
                          CAMPAIGN_BRIEF_TEST_IDS.CREATOR_PAYMENTS__COMPENSATION_MAX
                        )}
                      </div>
                    </div>
                  </div>
                </CreatorCompensationRadioOption>
              </div>
              <div
                className={clsx(
                  styles.radioOptionContainer,
                  briefFieldManager.fields.creatorCompensationType.value ===
                    CREATOR_COMPENSATION_TYPES.FIXED && styles.selected
                )}
              >
                <CreatorCompensationRadioOption
                  checked={
                    briefFieldManager.fields.creatorCompensationType.value ===
                    CREATOR_COMPENSATION_TYPES.FIXED
                  }
                  disabled={!editable}
                  title={translate(`${TRANSLATION_PREFIX}.fixed-price`)}
                  info={translate(`${TRANSLATION_PREFIX}.fixed-price-info`)}
                  onClick={() =>
                    updateCreatorCompensationType(
                      CREATOR_COMPENSATION_TYPES.FIXED,
                      updateBriefForm
                    )
                  }
                >
                  <div className={`${styles.row} ${styles.rowGap}`}>
                    <label className={`${styles.info} ${styles.small}`}>
                      {translate(`${TRANSLATION_PREFIX}.enter-payment-amount`)}
                    </label>
                  </div>
                  <div className={`${styles.row} ${styles.rowGap}`}>
                    <div className={styles.column}>
                      <div className={`${styles.cashValue} ${styles.big}`}>
                        {getCashValue(
                          '',
                          'fixedCostForCreations',
                          budgetRangeData.fixed,
                          CAMPAIGN_BRIEF_TEST_IDS.CREATOR_PAYMENTS__COMPENSATION_FIXED
                        )}
                      </div>
                    </div>
                  </div>
                </CreatorCompensationRadioOption>
              </div>
            </div>
          )}
          {allowAffiliateCampaigns && (
            <CreatorCompensationCheckboxOption
              disabled={!hasShopifyAccessToken}
              checked={
                !!briefFieldManager.fields.creatorCompensationAffiliateSales
                  .value
              }
              onChange={(checked: boolean) =>
                handleAffiliateSalesChange(checked)
              }
              title={translate(`${TRANSLATION_PREFIX}.affiliate-sales`)}
              info={translate(`${TRANSLATION_PREFIX}.affiliate-sales-info`)}
            >
              <div className={styles.affiliateSalesContainer}>
                <ValidatedField errors={errors?.commissionRate || []}>
                  <div className={styles.commissionPercentageOptionsContainer}>
                    {AFFILIATE_SALES_PERCENTAGES.map((percentage) => {
                      const isSelected =
                        briefFieldManager.fields.commissionRate.value ===
                        percentage;
                      return (
                        <div
                          className={clsx(
                            styles.radioOptionContainer,
                            isSelected && styles.selected
                          )}
                          key={percentage}
                        >
                          <div className={styles.commissionPercentage}>
                            <Checkbox
                              radio
                              checked={isSelected}
                              onChange={() => {
                                if (updateBriefForm) {
                                  updateBriefForm(
                                    'commissionRate',
                                    percentage,
                                    'commissionRate',
                                    DEFAULT_SAVE_CAMPAIGN_DELAY
                                  );
                                }
                              }}
                              label={
                                <label className={styles.title}>
                                  {percentage}%
                                </label>
                              }
                            />
                          </div>
                        </div>
                      );
                    })}
                    <div
                      className={clsx(
                        styles.radioOptionContainer,
                        isOtherCommissionSelected && styles.selected
                      )}
                    >
                      <div className={styles.commissionPercentage}>
                        <Checkbox
                          radio
                          checked={isOtherCommissionSelected}
                          onChange={handleCustomCommissionClick}
                          label={
                            <div className={styles.percentageInputContainer}>
                              <BriefFieldInput
                                placeholder={translate(
                                  `${TRANSLATION_PREFIX}.other-commission`
                                )}
                                endIcon="%"
                                onInputChange={(inputValue) => {
                                  setCustomCommissionRateInput(inputValue);
                                  if (
                                    updateBriefForm &&
                                    isOtherCommissionSelected
                                  ) {
                                    updateBriefForm(
                                      'commissionRate',
                                      inputValue,
                                      'commissionRate',
                                      DEFAULT_SAVE_CAMPAIGN_DELAY
                                    );
                                  }
                                }}
                                inputRef={commissionInputRef}
                                inputProps={{
                                  onInput: (e) => {
                                    if (e.currentTarget.value.length > 2) {
                                      e.currentTarget.value =
                                        e.currentTarget.value.slice(0, 2);
                                    }
                                  },
                                  onKeyDown: (e) => {
                                    if (
                                      ['e', 'E', '+', '-', '.'].includes(e.key)
                                    ) {
                                      e.preventDefault();
                                    }
                                  },
                                  type: 'number',
                                  value: customCommissionRateInput,
                                  onBlur: () => {
                                    saveCampaign?.({
                                      campaignId,
                                      fieldName:
                                        briefFieldManager.fields.commissionRate
                                          .key,
                                      fieldValue:
                                        briefFieldManager.fields.commissionRate
                                          .value,
                                    });
                                  },
                                }}
                                hasErrors={!!errors?.commissionRate?.length}
                              />
                            </div>
                          }
                        />
                      </div>
                    </div>
                  </div>
                </ValidatedField>
                <label className={styles.info}>
                  {translate(
                    `${TRANSLATION_PREFIX}.affiliate-sales-info-returns`
                  )}
                </label>
              </div>
            </CreatorCompensationCheckboxOption>
          )}
        </div>
        {allowAffiliateCampaigns && !hasShopifyAccessToken && (
          <>
            <div className={styles.shopifyWarningContainer}>
              <div className={styles.shopifyWarningContent}>
                <ErrorOutlineIcon />
                <label className={styles.info}>
                  {translate(`${TRANSLATION_PREFIX}.shopify-warning`)}
                </label>
              </div>
              <button
                type="button"
                className={styles.shopifyLinkButton}
                onClick={() => setIsShopifyLinkModalOpen(true)}
              >
                {translate(`${TRANSLATION_PREFIX}.shopify-link`)}
              </button>
            </div>
            <PaymentRecap briefFieldManager={briefFieldManager} />
          </>
        )}
      </div>
    </BriefSubSection>
  );
}

function CreatorPayments(props: SectionProps) {
  const {
    updateBriefForm,
    editable,
    saveCampaign,
    briefFieldManager,
    errors,
    campaign,
    allowAffiliateCampaigns,
  } = props;

  return (
    <div className={styles.container}>
      <CreatorCompensation
        editable={editable}
        budgetMin={briefFieldManager.fields.suggestedCostForCreationsMin.value}
        budgetMax={briefFieldManager.fields.suggestedCostForCreations.value}
        budgetFixed={briefFieldManager.fields.fixedCostForCreations.value}
        onChange={(path: string, value: number) => {
          updateBriefForm?.(path, value, path);
        }}
        saveCampaign={saveCampaign}
        errors={errors}
        briefFieldManager={briefFieldManager}
        updateBriefForm={updateBriefForm}
        campaignId={campaign.id}
        hasShopifyAccessToken={!!campaign.brand.hasShopifyAccessToken}
        brandId={campaign.brand.id}
        allowAffiliateCampaigns={allowAffiliateCampaigns}
      />
    </div>
  );
}

export default CreatorPayments;
